import React, { useRef } from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import {
    FaOptionIcon,
    editIcon,
    userIcon as contactIcon,
    companyIcon,
} from '@europrocurement/l2d-icons';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useContextMenu } from '@europrocurement/l2d-hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMemo } from '@fortawesome/sharp-regular-svg-icons';
import { ActivityProps, ActionTypeProps } from '../../../types/ActivityHistoryTypes';
import { DisplayFile } from '../../atoms';
import { NoteContent } from '../../atoms/NoteContent/NoteContent';
import { useNotePermissions } from '../../../hooks/useNotePermissions';
import { CustomAvatar } from '../CustomAvatar';
import { NoteToolbox } from '../NoteToolbox/NoteToolbox';
import { NoteEditedIndicator } from '../NoteEditedIndicator/NoteEditedIndicator';
import { NoteContextMenu } from '../ContactInformations/fragments/NoteContextMenu';

export type ActivityTimelineItemProps = {
    activity: ActivityProps;
    isLast: boolean;
    onEditNote?: (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => void;
    onDeleteNote?: (noteId: string) => void;
};

const getActionIcon = (actionType: ActionTypeProps) => {
    switch (actionType) {
        case 'edit':
            return editIcon;
        case 'create_contact':
            return contactIcon;
        case 'create_company':
            return companyIcon;
        default:
            return editIcon;
    }
};

export const ActivityTimelineItem: React.FunctionComponent<ActivityTimelineItemProps> = function ({
    activity,
    isLast,
    onEditNote,
    onDeleteNote,
}) {
    const theme = useTheme();
    const [isHovered, setIsHovered] = React.useState(false);
    const noteContentRef = useRef<HTMLDivElement>(null);
    const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();

    const notePermissions = useNotePermissions(
        activity.type === 'note' ? activity.userId || '' : '',
    );

    const shouldShowToolbox =
        activity.type === 'note' &&
        isHovered &&
        notePermissions.hasAnyPermission &&
        !!(onEditNote || onDeleteNote) &&
        !contextMenu.open;

    const isNoteEdited =
        activity.type === 'note' &&
        activity.updatedAt &&
        activity.updatedAt.getTime() !== activity.createdAt.getTime();

    const handleEditNote = () => {
        if (activity.type === 'note' && onEditNote) {
            onEditNote(
                activity.id,
                activity.content || '',
                activity.noteType === 'important',
                activity.memoClient,
            );
        }
    };

    const handleDeleteNote = () => {
        if (activity.type === 'note' && onDeleteNote) {
            onDeleteNote(activity.id);
        }
    };

    const handleNoteContextMenu = (event: React.MouseEvent) => {
        if (activity.type === 'note' && notePermissions.hasAnyPermission) {
            openContextMenu(event);
        }
    };

    const handleItemClick = () => {
        if (contextMenu.open) {
            closeContextMenu();
        }
    };

    const timeAgo = formatDistanceToNow(activity.createdAt, {
        addSuffix: true,
        locale: fr,
    });

    const userName = activity.userLastName
        ? `${activity.userFirstName} ${activity.userLastName}`
        : activity.userFirstName;

    return (
        <>
            <Box
                sx={{ display: 'flex', position: 'relative', minHeight: '60px' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleItemClick}
            >
                {!isLast && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '20px',
                            top: '40px',
                            bottom: '-20px',
                            width: '2px',
                            backgroundColor: theme.palette.divider,
                            zIndex: 0,
                        }}
                    />
                )}

                <Box sx={{ marginRight: '16px', zIndex: 1 }}>
                    {activity.type === 'note' ? (
                        <CustomAvatar
                            firstName={activity.userFirstName}
                            lastName={activity.userLastName || 'Inconnu'}
                            keycloakId={activity.userId}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '40px',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                                color: theme.palette.secondary.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `2px solid ${theme.palette.background.default}`,
                            }}
                        >
                            <FaOptionIcon
                                {...getActionIcon(activity.actionType).props}
                                sx={{ fontSize: '8px', color: theme.palette.secondary.main }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1, paddingBottom: '20px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            variant="l2dbodys"
                            sx={{
                                color: theme.palette.text.secondary,
                                marginBottom: '8px',
                                fontWeight: 600,
                            }}
                        >
                            {userName},
                        </Typography>
                        <Typography
                            variant="l2dbodyxs"
                            sx={{
                                color: theme.palette.text.secondary,
                                marginBottom: '8px',
                                fontWeight: 500,
                            }}
                        >
                            {timeAgo}
                        </Typography>
                    </Box>

                    {activity.type === 'note' ? (
                        <>
                            <Box
                                ref={noteContentRef}
                                onContextMenu={handleNoteContextMenu}
                                sx={{
                                    padding: '12px 16px',
                                    backgroundColor:
                                        activity.noteType === 'important'
                                            ? alpha(theme.palette.primary.main, 0.2)
                                            : theme.palette.background.default,
                                    border:
                                        activity.noteType === 'important'
                                            ? `1px solid ${theme.palette.background.default}`
                                            : 'none',
                                    borderRadius: '8px',
                                    marginBottom: activity.attachment ? '12px' : 0,
                                    boxShadow: 'none',
                                    position: 'relative',
                                    width: 'fit-content',
                                    paddingBottom: isNoteEdited ? '24px' : '12px',
                                    ...(contextMenu.open && {
                                        backgroundColor:
                                            activity.noteType === 'important'
                                                ? theme.palette.primary.main
                                                : theme.palette.action.selected,
                                    }),
                                }}
                            >
                                {activity.memoClient && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '-6px',
                                            left: '-6px',
                                            backgroundColor: theme.palette.background.default,
                                            borderRadius: '50%',
                                            padding: '5px',
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            fontSize: '10px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faMemo} />
                                    </Box>
                                )}
                                <NoteContent
                                    content={activity.content || ''}
                                    sx={{
                                        color: theme.palette.text.primary,
                                    }}
                                />

                                {isNoteEdited && (
                                    <NoteEditedIndicator
                                        updatedAt={activity.updatedAt!}
                                        updatedByFirstName={activity.updatedByFirstName!}
                                        updatedByLastName={activity.updatedByLastName!}
                                    />
                                )}

                                <NoteToolbox
                                    visible={shouldShowToolbox}
                                    onEdit={handleEditNote}
                                    onDelete={handleDeleteNote}
                                    parentRef={noteContentRef}
                                />
                            </Box>

                            {activity.attachment && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'left',
                                        alignItems: 'center',
                                    }}
                                >
                                    <DisplayFile
                                        nom="Informations juridiques"
                                        styleSize="small"
                                        size={2150000}
                                        mimetype="application/pdf"
                                        mode="card"
                                        color={theme.palette.primary.main}
                                        backgroundColor={theme.palette.primary.light}
                                    />
                                    <DisplayFile
                                        nom="Capture d'écran"
                                        styleSize="small"
                                        size={46254}
                                        mimetype="image/jpeg"
                                        mode="card"
                                        color={theme.palette.primary.main}
                                        backgroundColor={theme.palette.primary.light}
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.primary,
                            }}
                        >
                            {activity.description}
                        </Typography>
                    )}
                </Box>
            </Box>

            {activity.type === 'note' && (
                <NoteContextMenu
                    contextMenu={contextMenu}
                    onClose={closeContextMenu}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                    canEdit={notePermissions.canEdit}
                    canDelete={notePermissions.canDelete}
                    noteContent={activity.content || ''}
                    createdBy={userName}
                    createdAt={activity.createdAt}
                    isImportant={activity.noteType === 'important'}
                />
            )}
        </>
    );
};
